import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import { Label } from "../../../../../components/ui/label";
import { Input } from "../../../../../components/ui/input";
import { Switch } from "../../../../../components/ui/switch";
import { Button } from "../../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Trash2, Star } from "lucide-react";
import { Region } from "../../../../../types/enums";
import { LIECHTENSTEIN_VAT_NUMBER_FORMAT, RegionMetadata, RegionConfig, ROUND_TO_OPTIONS } from "../../../../../types/region.types";
import { CountryEnum } from "@/lib/supabase/supabaseEnums";
import { useCentralStore } from "@/dentlab/src/store/Central";

interface RegionCardProps {
	region: RegionConfig;
	index: number;
	regionInfo: RegionMetadata | undefined;
	isDefault: boolean;
	onUpdateField: (index: number, field: keyof RegionConfig, value: any) => void;
	onRemove: (index: number) => void;
}

export const RegionCard: React.FC<RegionCardProps> = memo(({
	region,
	index,
	regionInfo,
	isDefault,
	onUpdateField,
	onRemove,
}) => {
	const isLiechtenstein = useCentralStore(
		(state) => state.organization?.country === CountryEnum.LIECHTENSTEIN
	);
	// Local state for input values to prevent re-renders on every keystroke
	const [localValues, setLocalValues] = useState({
		mwst_number: region.mwst_number || "",
		mwst_percentage: region.mwst_percentage?.toString() || "",
		currency_text: region.currency_text || "",
	});

	const debounceTimers = useRef<{ [key: string]: NodeJS.Timeout }>({});

	// Update local state when region prop changes (e.g., after save)
	useEffect(() => {
		setLocalValues({
			mwst_number: region.mwst_number || "",
			mwst_percentage: region.mwst_percentage?.toString() || "",
			currency_text: region.currency_text || "",
		});
	}, [region.mwst_number, region.mwst_percentage, region.currency_text]);

	// Handle local input changes
	const handleLocalChange = useCallback((field: keyof typeof localValues, value: string) => {
		setLocalValues(prev => ({ ...prev, [field]: value }));

		// Clear existing timer
		const timerKey = `${field}`;
		if (debounceTimers.current[timerKey]) {
			clearTimeout(debounceTimers.current[timerKey]);
		}

		// Debounce the actual update to the store
		debounceTimers.current[timerKey] = setTimeout(() => {
			if (field === 'mwst_percentage') {
				onUpdateField(index, field, value ? parseFloat(value) : null);
			} else {
				onUpdateField(index, field as keyof RegionConfig, value);
			}
			delete debounceTimers.current[timerKey];
		}, 800);
	}, [index, onUpdateField]);

	// Cleanup timers on unmount
	useEffect(() => {
		return () => {
			Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
		};
	}, []);

	const handleSwitchChange = useCallback((checked: boolean) => {
		onUpdateField(index, "is_mwst_eust", checked);
	}, [index, onUpdateField]);

	const handleRoundingChange = useCallback((value: string) => {
		onUpdateField(index, "round_to", parseInt(value));
	}, [index, onUpdateField]);

	const handleRemove = useCallback(() => {
		onRemove(index);
	}, [index, onRemove]);

	return (
		<div className="p-4">
			<div className="flex items-start justify-between mb-3">
				<div className="flex items-center gap-2">
					<span className="font-medium">{regionInfo?.label}</span>
					{isDefault && (
						<span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
							<Star className="h-3 w-3" />
							Standard
						</span>
					)}
				</div>
				<Button
					variant="ghost"
					size="sm"
					onClick={handleRemove}
					className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
				>
					<Trash2 className="h-4 w-4" />
				</Button>
			</div>

			<div className="grid gap-3 md:grid-cols-3">
				<div>
					<Label htmlFor={`mwst-number-${region.region}`} className="text-xs">
						MwSt.-Nummer
					</Label>
					<Input
						id={`mwst-number-${region.region}`}
						type="text"
						className="h-9 text-sm"
						value={localValues.mwst_number}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							handleLocalChange("mwst_number", e.target.value)
						}
						placeholder={
							region.region === Region.CH
								? isLiechtenstein
									? LIECHTENSTEIN_VAT_NUMBER_FORMAT
									: "CHE-123.456.789 MWST"
								: "DE123456789"
						}
					/>
				</div>

				<div>
					<Label htmlFor={`mwst-percentage-${region.region}`} className="text-xs">
						MwSt.-Satz (%)
					</Label>
					<Input
						id={`mwst-percentage-${region.region}`}
						type="number"
						className="h-9 text-sm"
						step="0.1"
						min="0"
						max="100"
						value={localValues.mwst_percentage}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							handleLocalChange("mwst_percentage", e.target.value)
						}
						placeholder={`${regionInfo?.defaultVat}%`}
					/>
				</div>

				<div className="flex items-center gap-2">
					<Switch
						id={`eust-${region.region}`}
						checked={region.is_mwst_eust}
						onCheckedChange={handleSwitchChange}
					/>
					<Label htmlFor={`eust-${region.region}`} className="text-xs cursor-pointer">
						Wir nutzen EUSt. statt MwSt.
					</Label>
				</div>
			</div>

			<div className="grid gap-3 md:grid-cols-2 mt-3">
				<div>
					<Label htmlFor={`currency-${region.region}`} className="text-xs">
						Währung
					</Label>
					<Input
						id={`currency-${region.region}`}
						type="text"
						className="h-9 text-sm"
						value={localValues.currency_text}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
							handleLocalChange("currency_text", e.target.value)
						}
						placeholder={region.region === Region.CH ? "CHF" : "€"}
					/>
				</div>

				<div>
					<Label htmlFor={`rounding-${region.region}`} className="text-xs">
						Rundung
					</Label>
					<Select
						value={String(region.round_to ?? 4)}
						onValueChange={handleRoundingChange}
					>
						<SelectTrigger className="h-9 text-sm">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{ROUND_TO_OPTIONS.map((option) => (
								<SelectItem key={option.value} value={String(option.value)}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>
		</div>
	);
}, (prevProps, nextProps) => {
	// Custom comparison function for React.memo
	// Only re-render if these specific props change
	return (
		prevProps.region.region === nextProps.region.region &&
		prevProps.region.is_mwst_eust === nextProps.region.is_mwst_eust &&
		prevProps.region.round_to === nextProps.region.round_to &&
		prevProps.isDefault === nextProps.isDefault &&
		prevProps.index === nextProps.index
	);
});

RegionCard.displayName = "RegionCard";